// Signed TrailBivvy Pro entitlement tokens (decisions.md PWA-12).
//
// A token is a compact JWS (RFC 7515) signed with ECDSA P-256 / SHA-256 ("ES256"). The Worker signs with its private
// key; the app verifies offline with the public keys compiled into it (src/store/keys.ts), so a hiker keeps access on
// the trail without a connection and a token cannot be edited on the device without the signature breaking.
// Only WebCrypto is used, so the same module runs in the browser, in the Worker and in Node tests.

export interface EntitlementClaims {
  /** Token format version. */
  v: 1;
  /** Issuer origin, e.g. "https://app.trailbivvy.com". */
  iss: string;
  /** Stripe customer id. */
  sub: string;
  /** Stripe subscription id. */
  sid: string;
  /** Stripe subscription status at issue time. */
  st: StripeSubscriptionStatus;
  /** End of the current paid or trial period (epoch seconds). */
  pe: number;
  /** Start of the current period (epoch seconds). */
  ps: number;
  /** The current period is the free trial. */
  tr: boolean;
  /** Set to renew at the period end (false once cancelled). */
  wr: boolean;
  /** The first charge after the free trial failed (status past_due right after the trial). */
  ft: boolean;
  /** Issued at (epoch seconds). */
  iat: number;
  /** Last instant this token grants access (epoch seconds), computed with src/store/rules.ts. */
  exp: number;
}

export type StripeSubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlDecode(text: string): Uint8Array<ArrayBuffer> {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((text.length + 3) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

const algorithm = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const signAlgorithm = { name: 'ECDSA', hash: 'SHA-256' } as const;

export async function importPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, algorithm, false, ['sign']);
}

export async function importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  const { kty, crv, x, y } = jwk;
  return crypto.subtle.importKey('jwk', { kty, crv, x, y }, algorithm, false, ['verify']);
}

/** Generates a key pair (scripts/gen-entitlement-keys.mjs and tests). */
export async function generateKeyPair(): Promise<{ privateJwk: JsonWebKey; publicJwk: JsonWebKey }> {
  const pair = (await crypto.subtle.generateKey(algorithm, true, ['sign', 'verify'])) as CryptoKeyPair;
  const privateJwk = (await crypto.subtle.exportKey('jwk', pair.privateKey)) as JsonWebKey;
  const full = (await crypto.subtle.exportKey('jwk', pair.publicKey)) as JsonWebKey;
  return { privateJwk, publicJwk: { kty: full.kty, crv: full.crv, x: full.x, y: full.y } };
}

export async function signToken(claims: EntitlementClaims, privateKey: CryptoKey, kid: string): Promise<string> {
  const header = base64UrlEncode(encoder.encode(JSON.stringify({ alg: 'ES256', typ: 'JWT', kid })));
  const payload = base64UrlEncode(encoder.encode(JSON.stringify(claims)));
  const signature = await crypto.subtle.sign(signAlgorithm, privateKey, encoder.encode(`${header}.${payload}`));
  return `${header}.${payload}.${base64UrlEncode(new Uint8Array(signature))}`;
}

export type TokenVerification =
  | { ok: true; claims: EntitlementClaims; kid: string }
  | { ok: false; reason: 'malformed' | 'unknownKey' | 'badSignature' | 'wrongIssuer' };

/**
 * Verifies the signature against the trusted public keys (by `kid`) and the issuer. Expiry is NOT checked here: an
 * expired token still tells the paywall what the subscription was ("ended on …"); access is decided by rules.ts.
 */
export async function verifyToken(
  token: string,
  publicKeys: Readonly<Record<string, JsonWebKey>>,
  expectedIssuers: readonly string[],
): Promise<TokenVerification> {
  const parts = token.split('.');
  if (parts.length !== 3) return { ok: false, reason: 'malformed' };
  let header: { alg?: string; kid?: string };
  let claims: EntitlementClaims;
  try {
    header = JSON.parse(decoder.decode(base64UrlDecode(parts[0])));
    claims = JSON.parse(decoder.decode(base64UrlDecode(parts[1])));
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (header.alg !== 'ES256' || typeof header.kid !== 'string' || claims?.v !== 1) return { ok: false, reason: 'malformed' };
  const jwk = publicKeys[header.kid];
  if (!jwk) return { ok: false, reason: 'unknownKey' };
  let valid = false;
  try {
    const key = await importPublicKey(jwk);
    valid = await crypto.subtle.verify(signAlgorithm, key, base64UrlDecode(parts[2]), encoder.encode(`${parts[0]}.${parts[1]}`));
  } catch {
    valid = false;
  }
  if (!valid) return { ok: false, reason: 'badSignature' };
  if (!expectedIssuers.includes(claims.iss)) return { ok: false, reason: 'wrongIssuer' };
  return { ok: true, claims, kid: header.kid };
}
